import { SERIES_COLORS } from "@/constants/chart-constants.js";
import { toFixedSafe } from "@/lib/formatting-utils.js";

const TOOLTIP_MUTED = "hsl(var(--muted-foreground))";
const TOOLTIP_FG = "hsl(var(--foreground))";

/** @type {Intl.DateTimeFormatOptions} */
const TOOLTIP_DATE_OPTIONS = {
  month: "short",
  day: "numeric",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
};

/**
 * Chart tooltip timestamp: `Jan 10, 25 11:25 PM` (local).
 * @param {number|string|Date} value
 * @returns {string}
 */
export function formatChartTooltipDateTime(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date
    .toLocaleString("en-US", TOOLTIP_DATE_OPTIONS)
    .replace(/,(?=\s*\d{1,2}:\d{2}\s*(?:AM|PM))/i, "")
    .replace(/\s+at\s+/i, " ");
}

/**
 * @param {{ timestamp: string|number|Date, value: number, textValue?: string }} point
 * @returns {[number, number]|{ x: number, y: number, custom: { textValue: string } }}
 */
function toSeriesPoint(point) {
  const x = new Date(point.timestamp).getTime();
  const y = point.value;
  if (point.textValue) {
    return { x, y, custom: { textValue: point.textValue } };
  }
  return [x, y];
}

/**
 * @param {unknown} point
 * @returns {string|undefined}
 */
function getPointTextValue(point) {
  return (
    point?.point?.custom?.textValue
    ?? point?.point?.options?.custom?.textValue
    ?? point?.custom?.textValue
    ?? point?.options?.custom?.textValue
  );
}

/**
 * One Y-axis index per unique unit (sorted for stable order).
 * @param {Array} data
 * @param {Map<string, number>|null} [unitToAxisMap]
 * @returns {Map<string, number>}
 */
function createUnitToAxisMap(data, unitToAxisMap = null) {
  if (unitToAxisMap) return unitToAxisMap;

  const units = [
    ...new Set(
      data.map((item) => item.param?.unit || "").filter(Boolean)
    ),
  ].sort();

  return new Map(units.map((unit, index) => [unit, index]));
}

/**
 * Create chart series from trend data (one Y-axis per unique unit).
 * @param {Array} data
 * @param {Map<string, number>|null} [unitToAxisMap]
 * @returns {{
 *   series: Object[],
 *   unitAxisAssignments: Array<{ unit: string, axisIndex: number }>,
 *   numAxes: number,
 *   hasMultipleAxes: boolean,
 * }}
 */
export function createChartSeries(data, unitToAxisMap = null) {
  const unitAxisMap = createUnitToAxisMap(data, unitToAxisMap);

  const series = data.map((item, index) => {
    const unit = item.param?.unit || "";
    const yAxisIndex =
      unit && unitAxisMap.has(unit) ? unitAxisMap.get(unit) : 0;

    return {
      name: item.param.parameter,
      data: item.data.map(toSeriesPoint),
      color: SERIES_COLORS[index % SERIES_COLORS.length],
      yAxis: yAxisIndex,
      lineWidth: 2,
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: true,
            radius: 5,
            lineWidth: 2,
          },
        },
      },
    };
  });

  const unitAxisAssignments = [...unitAxisMap.entries()]
    .map(([unit, axisIndex]) => ({ unit, axisIndex }))
    .sort((a, b) => a.axisIndex - b.axisIndex);

  const numAxes = unitAxisAssignments.length;

  return {
    series,
    unitAxisAssignments,
    numAxes,
    hasMultipleAxes: numAxes > 1,
  };
}

/**
 * @param {number|null|undefined} value
 * @returns {string|number|null|undefined}
 */
export function formatTooltipValue(value) {
  return toFixedSafe(value, 2);
}

/** Escape text embedded in Highcharts HTML tooltips */
export function escapeTooltipHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Highcharts tooltip chrome readable in light and dark themes.
 * Dark mode uses an elevated panel (lighter than --card) so the tip
 * does not blend into the chart surface.
 * @returns {{ backgroundColor: string, borderColor: string }}
 */
export function getChartTooltipChrome() {
  const isDark =
    typeof document !== "undefined"
    && document.documentElement.classList.contains("dark");
  if (isDark) {
    return {
      backgroundColor: "hsl(220 14% 24%)",
      borderColor: "hsl(220 12% 36%)",
    };
  }
  return {
    backgroundColor: "hsl(var(--card))",
    borderColor: "hsl(var(--border))",
  };
}

/**
 * Shared multi-series Highcharts tooltip formatter.
 * @param {Array} trendData
 * @returns {Function}
 */
export function formatMultiSeriesTooltip(trendData) {
  return function () {
    const rows = [
      `<p style="font-size: 12px; color: ${TOOLTIP_MUTED}; margin-bottom: 8px;">${formatChartTooltipDateTime(this.x)}</p>`,
    ];

    this.points.forEach((point) => {
      const param = trendData.find(
        (item) => item.param.parameter === point.series.name
      )?.param;
      const unit = param?.unit || "";
      const formattedValue = formatTooltipValue(point.y);
      const textValue = getPointTextValue(point);

      rows.push(`<p style="font-size: 14px; font-weight: 500; color: ${TOOLTIP_FG}; margin-bottom: 4px;">
        <span style="display: inline-block; width: 10px; height: 10px; background-color: ${point.color}; margin-right: 6px; border-radius: 2px;"></span>
        ${point.series.name}: <strong>${formattedValue} ${unit}</strong>
      </p>`);

      if (textValue) {
        rows.push(
          `<p style="font-size: 12px; color: ${TOOLTIP_MUTED}; margin: 0 0 4px 16px;">${escapeTooltipHtml(textValue)}</p>`
        );
      }
    });

    return `<div style="padding: 12px;">${rows.join("")}</div>`;
  };
}
